/**
 * The facility switcher's directory (V6, design facility-directory.js): one place that knows
 * each country's own levels (US: Region › State › City) and which buildings a picked scope
 * covers. Console and phone use the same rules.
 *
 * Only the rules are here. The buildings come from our own organizations (never the design's
 * sample rows): a country appears only once it has a building, so nothing is invented.
 */

export interface CountryConfig {
  name: string;
  /** The levels under the country, top first (US: Region, State, City). */
  levels: [string, string, string];
  currency: string;
  /** The privacy law that applies there. */
  privacy: string;
}

export const COUNTRIES: Readonly<Record<string, CountryConfig>> = {
  US: { name: 'United States', levels: ['Region', 'State', 'City'], currency: 'USD', privacy: 'HIPAA' },
  CA: { name: 'Canada', levels: ['Region', 'Province', 'City'], currency: 'CAD', privacy: 'PIPEDA' },
  GB: { name: 'United Kingdom', levels: ['Nation', 'County', 'Town'], currency: 'GBP', privacy: 'UK GDPR' },
  DE: { name: 'Germany', levels: ['Region', 'State', 'City'], currency: 'EUR', privacy: 'GDPR' },
  FR: { name: 'France', levels: ['Region', 'Department', 'City'], currency: 'EUR', privacy: 'GDPR' },
  ES: { name: 'Spain', levels: ['Region', 'Province', 'City'], currency: 'EUR', privacy: 'GDPR' },
  JP: { name: 'Japan', levels: ['Region', 'Prefecture', 'City'], currency: 'JPY', privacy: 'APPI' },
  KR: { name: 'South Korea', levels: ['Region', 'Province', 'City'], currency: 'KRW', privacy: 'PIPA' },
  SG: { name: 'Singapore', levels: ['Region', 'District', 'Area'], currency: 'SGD', privacy: 'PDPA' },
  AU: { name: 'Australia', levels: ['Region', 'State', 'City'], currency: 'AUD', privacy: 'Privacy Act' },
};

/** One building as the switcher places it. */
export interface DirectoryFacility {
  id?: string;
  name: string;
  country: string;
  region: string;
  state: string;
  city: string;
}

/** What is picked in the switcher (console `scope`, phone `msc`). Empty means all facilities. */
export interface FacilityScope {
  country?: string;
  region?: string;
  state?: string;
  city?: string;
  /** One building, by name. */
  fac?: string;
}

/** The buildings a scope covers. Pure. */
export function scopeFacilities(list: readonly DirectoryFacility[], sc: FacilityScope = {}): DirectoryFacility[] {
  return list.filter((f) => (!sc.country || f.country === sc.country)
    && (!sc.region || f.region === sc.region)
    && (!sc.state || f.state === sc.state)
    && (!sc.city || f.city === sc.city)
    && (!sc.fac || f.name === sc.fac));
}

/** True when nothing is picked (every facility). */
export function isAllFacilities(sc: FacilityScope = {}): boolean {
  return !(sc.country || sc.region || sc.state || sc.city || sc.fac);
}

/**
 * Whether a building (by name or a short name staff use) is inside the picked scope. With no
 * scope everything is in. A name the directory does not know is out once a scope is picked.
 */
export function inFacilityScope(
  list: readonly DirectoryFacility[], sc: FacilityScope, name: string, alias: Readonly<Record<string, string>> = {},
): boolean {
  if (!sc || !(sc.country || sc.fac)) return true;
  const lc = String(name ?? '').toLowerCase();
  const full = alias[lc] ?? list.find((f) => f.name.toLowerCase() === lc)?.name ?? name;
  if (!list.some((f) => f.name === full)) return false;
  return scopeFacilities(list, sc).some((f) => f.name === full);
}

/** The countries that have at least one building, in the order they are configured. */
export function countriesWithFacilities(list: readonly DirectoryFacility[]): string[] {
  const have = new Set(list.map((f) => f.country));
  return Object.keys(COUNTRIES).filter((c) => have.has(c));
}

/** The distinct values one level down from a scope (regions of a country, states of a region, ...), sorted. */
export function nextLevelValues(list: readonly DirectoryFacility[], sc: FacilityScope): { level: keyof FacilityScope; values: string[] } | null {
  const order: Array<keyof DirectoryFacility & keyof FacilityScope> = ['country', 'region', 'state', 'city'];
  const pickedDepth = order.filter((k) => sc[k]).length;
  if (sc.fac || pickedDepth >= order.length) return null;
  const level = order[pickedDepth];
  const values = [...new Set(scopeFacilities(list, sc).map((f) => f[level]))].sort();
  return { level, values };
}

/** The level name shown for a depth in a country ("Region", "Province", "Town" ...). */
export function levelName(country: string | undefined, level: 'region' | 'state' | 'city'): string {
  const c = country ? COUNTRIES[country] : undefined;
  const i = level === 'region' ? 0 : level === 'state' ? 1 : 2;
  return c?.levels[i] ?? ['Region', 'State', 'City'][i];
}

/** "Brookdale Westlake", "Austin, TX", "South Central", "United States" or "All facilities". */
export function scopeLabel(sc: FacilityScope = {}): string {
  if (sc.fac) return sc.fac;
  if (sc.city) return sc.state ? `${sc.city}, ${sc.state}` : sc.city;
  if (sc.state) return sc.state;
  if (sc.region) return sc.region;
  if (sc.country) return COUNTRIES[sc.country]?.name ?? sc.country;
  return 'All facilities';
}

/** One building as GET /facility-directory returns it (src/aithora/facility-directory.ts). */
export interface DirectoryRow extends DirectoryFacility {
  id: string;
  /** Where the region came from: Super Admin's choice, the US Census default, or none yet. */
  regionSource: 'operator' | 'census' | 'none';
}
/** A building with no known address is listed under this, so nothing Aithora serves is hidden. */
export const NO_LOCATION = 'Location not set';
